import { api } from './axios'
import type {
  BulkInviteListFilters,
  BulkInviteMappingInspection,
  BulkInviteMappingOverride,
  BulkInvitePreview,
  BulkInviteQueued,
  BulkInviteRequestsResponse,
  BulkInviteRowDetailsFilters,
  BulkInviteRowDetailsResponse,
} from './types'

const BULK_INVITE_PATH = '/users/invitations/bulk'

const MULTIPART_HEADERS = { 'Content-Type': 'multipart/form-data' }

/**
 * The file, plus the admin's column-mapping override when they made one, plus the
 * rows they removed on the review screen. Both extras are JSON-encoded into their
 * own form fields alongside the file — an absent `mapping` means "auto-map"; an
 * absent/empty `skipRows` means "invite everything the file resolves to".
 */
const toFormData = (
  file: File,
  mapping?: BulkInviteMappingOverride,
  skipRows?: number[],
): FormData => {
  const formData = new FormData()

  formData.append('file', file)
  if (mapping) formData.append('mapping', JSON.stringify(mapping))
  if (skipRows?.length) formData.append('skipRows', JSON.stringify(skipRows))

  return formData
}

/**
 * Inspect how the file's columns map to canonical fields — the cheap dry-run.
 * Calls POST /users/invitations/bulk?dryRun=mapping → 200.
 *
 * Cheap by design: parse + map only, no DB and no Cognito. This drives the Review
 * Column Mapping screen, whose edits are then held client-side — never re-call
 * this (or the full dry run) per dropdown change.
 */
export const inspectBulkInviteMapping = async (
  file: File,
): Promise<BulkInviteMappingInspection> => {
  const response = await api.post<BulkInviteMappingInspection>(BULK_INVITE_PATH, toFormData(file), {
    params: { dryRun: 'mapping' },
    headers: MULTIPART_HEADERS,
  })

  return response.data
}

/**
 * Validate a bulk-invite file without sending anything — the full dry run.
 * Calls POST /users/invitations/bulk?dryRun=full → 200 breakdown.
 *
 * Expensive: a tenant-DB and Cognito lookup per email. Call once, when the admin
 * confirms the mapping.
 */
export const validateBulkInvite = async (
  file: File,
  mapping?: BulkInviteMappingOverride,
): Promise<BulkInvitePreview> => {
  const response = await api.post<BulkInvitePreview>(BULK_INVITE_PATH, toFormData(file, mapping), {
    params: { dryRun: 'full' },
    headers: MULTIPART_HEADERS,
  })

  return response.data
}

/**
 * Send a bulk-invite file (async). Calls POST /users/invitations/bulk → 202 queued.
 * The per-row outcome is NOT returned inline — read it from the status/list endpoint
 * or the BULK_INVITE_COMPLETED realtime event.
 */
export const sendBulkInvite = async (
  file: File,
  mapping?: BulkInviteMappingOverride,
  skipRows?: number[],
): Promise<BulkInviteQueued> => {
  const response = await api.post<BulkInviteQueued>(
    BULK_INVITE_PATH,
    toFormData(file, mapping, skipRows),
    { headers: MULTIPART_HEADERS },
  )

  return response.data
}

/**
 * Re-upload a corrected file into an existing request, overwriting it in place.
 * Calls POST /users/invitations/bulk/:requestId/reupload → 202 queued (same requestId).
 *
 * Allowed only from a terminal status (else BULK_INVITE_REUPLOAD_NOT_TERMINAL), and
 * open to any tenant admin or super admin — not just the original uploader.
 */
export const reuploadBulkInvite = async (
  requestId: string,
  file: File,
  mapping?: BulkInviteMappingOverride,
  skipRows?: number[],
): Promise<BulkInviteQueued> => {
  const response = await api.post<BulkInviteQueued>(
    `${BULK_INVITE_PATH}/${requestId}/reupload`,
    toFormData(file, mapping, skipRows),
    { headers: MULTIPART_HEADERS },
  )

  return response.data
}

/**
 * Per-row outcomes of one request (successes, failures and pre-dispatch skips
 * merged, sorted by email). Calls GET /users/invitations/bulk/:requestId/details —
 * paginated, tenant-scoped (404 only outside the caller's tenant).
 */
export const getBulkInviteRequestDetails = async (
  requestId: string,
  filters: BulkInviteRowDetailsFilters = {},
): Promise<BulkInviteRowDetailsResponse> => {
  const params = new URLSearchParams()

  if (filters.limit !== undefined) params.append('limit', String(filters.limit))
  if (filters.offset !== undefined) params.append('offset', String(filters.offset))
  if (filters.status?.length) params.append('status', filters.status.join(','))

  const queryString = params.toString()
  const url = queryString
    ? `${BULK_INVITE_PATH}/${requestId}/details?${queryString}`
    : `${BULK_INVITE_PATH}/${requestId}/details`

  const response = await api.get<BulkInviteRowDetailsResponse>(url)

  return response.data
}

/**
 * List the tenant's bulk requests (newest first, paginated).
 * Calls GET /users/invitations/bulk — tenant-wide, so this includes other admins'
 * uploads. Open to TENANT_ADMIN and SUPER_ADMIN (scoped by the x-tenant-id header,
 * which the axios interceptor sets on every request).
 */
export const listBulkInviteRequests = async (
  filters: BulkInviteListFilters = {},
): Promise<BulkInviteRequestsResponse> => {
  const params = new URLSearchParams()

  if (filters.limit !== undefined) params.append('limit', String(filters.limit))
  if (filters.offset !== undefined) params.append('offset', String(filters.offset))
  if (filters.status) params.append('status', filters.status)
  if (filters.initiatedBy) params.append('initiatedBy', filters.initiatedBy)

  const queryString = params.toString()
  const url = queryString ? `${BULK_INVITE_PATH}?${queryString}` : BULK_INVITE_PATH

  const response = await api.get<BulkInviteRequestsResponse>(url)

  return response.data
}

/* -------------------------------------------------------------------------- */
/*  Error parsing                                                             */
/* -------------------------------------------------------------------------- */

/** A single validation failure — either row-scoped (`row`) or quota-scoped (`field`). */
export interface BulkInviteFieldError {
  row?: number
  field?: string
  email?: string
  code: string
  message: string
}

export interface ParsedBulkInviteError {
  /** Discriminating top-level error code (e.g. BULK_INVITE_ROW_ERRORS), if present. */
  code: string | null
  /** A human-readable headline message. */
  message: string
  /** Row/field-level failures (shape B); empty for structural failures (shape A). */
  fieldErrors: BulkInviteFieldError[]
}

const UNKNOWN_ROW_ERROR_CODE = 'BULK_INVITE_ROW_ERROR'
const GENERIC_BULK_INVITE_ERROR =
  'Something went wrong while processing the file. Please try again.'

/**
 * Bulk-invite codes returned by the backend.
 *
 * The split below is the whole point of the non-blocking model, so keep it sharp:
 *  - **Structural** codes are the only ones that reject a file. They arrive as a 400.
 *  - **Row-level** codes never reject anything any more. They arrive as *data* — a
 *    reason on a preview row (`invalidRows[]` / `alreadyInvited[]`) or on a finished
 *    request's `skipped[]`/`errors[]`. A row-level code showing up in a 400 body means
 *    the backend is older than this client.
 */
export enum BulkInviteErrorCode {
  // Structural / file-level — the whole upload is rejected.
  INVALID_FILE_TYPE = 'BULK_INVITE_INVALID_FILE_TYPE',
  FILE_EMPTY = 'BULK_INVITE_FILE_EMPTY',
  ROW_LIMIT_EXCEEDED = 'BULK_INVITE_ROW_LIMIT_EXCEEDED',
  INVALID_HEADERS = 'BULK_INVITE_INVALID_HEADERS',
  PARSE_FAILED = 'BULK_INVITE_PARSE_FAILED',
  QUOTA_INSUFFICIENT = 'BULK_INVITE_QUOTA_INSUFFICIENT',
  /** A required field no column could be pointed at — the file genuinely lacks it. */
  UNMAPPED_REQUIRED_FIELD = 'BULK_INVITE_UNMAPPED_REQUIRED_FIELD',
  /** Two columns claim one field and the override didn't resolve it. */
  AMBIGUOUS_HEADERS = 'BULK_INVITE_AMBIGUOUS_HEADERS',
  /** The submitted mapping override is malformed or self-contradictory. */
  INVALID_MAPPING = 'BULK_INVITE_INVALID_MAPPING',
  /** Re-upload attempted while the request was still QUEUED/PROCESSING. */
  REUPLOAD_NOT_TERMINAL = 'BULK_INVITE_REUPLOAD_NOT_TERMINAL',
  /** @deprecated Row failures are data now, not a rejection. Kept to recognise old servers. */
  ROW_ERRORS = 'BULK_INVITE_ROW_ERRORS',
  // Row-level — a reason attached to a row, never a rejection.
  INVALID_NAME = 'BULK_INVITE_INVALID_NAME',
  INVALID_EMAIL = 'BULK_INVITE_INVALID_EMAIL',
  INVALID_PHONE = 'BULK_INVITE_INVALID_PHONE',
  INVALID_ROLE = 'BULK_INVITE_INVALID_ROLE',
  INVALID_STORAGE_QUOTA = 'BULK_INVITE_INVALID_STORAGE_QUOTA',
  INVALID_API_QUOTA = 'BULK_INVITE_INVALID_API_QUOTA',
  DUPLICATE_EMAIL_IN_FILE = 'BULK_INVITE_DUPLICATE_EMAIL_IN_FILE',
  EMAIL_CLAIMED = 'INVITATION_EMAIL_CLAIMED',
  EMAIL_CLAIMED_EXTERNAL = 'INVITATION_EMAIL_CLAIMED_EXTERNAL',
}

/**
 * Frontend-owned, user-facing copy for every known bulk-invite code.
 *
 * Row-level copy is phrased as a *description of that row*, not as an instruction to
 * fix the file: these now render beside rows we are skipping while the rest send.
 */
export const BULK_INVITE_ERROR_MESSAGES: Record<string, string> = {
  [BulkInviteErrorCode.INVALID_FILE_TYPE]: 'The file must be a .csv, .xlsx or .xls file.',
  [BulkInviteErrorCode.FILE_EMPTY]: 'The file has headers but no data rows.',
  [BulkInviteErrorCode.ROW_LIMIT_EXCEEDED]:
    'The file has more than 1,000 rows. Split it into smaller batches.',
  [BulkInviteErrorCode.INVALID_HEADERS]:
    'The header row couldn’t be read. Download the template to check the format.',
  [BulkInviteErrorCode.PARSE_FAILED]: 'The file couldn’t be read. Check it and try again.',
  [BulkInviteErrorCode.QUOTA_INSUFFICIENT]:
    'Your organization doesn’t have enough storage or API quota for this batch.',
  [BulkInviteErrorCode.UNMAPPED_REQUIRED_FIELD]:
    'A required column (first name, last name or email) isn’t in this file. Add it and upload again.',
  [BulkInviteErrorCode.AMBIGUOUS_HEADERS]:
    'Two columns map to the same field. Pick one on the column review step.',
  [BulkInviteErrorCode.INVALID_MAPPING]:
    'The column mapping couldn’t be applied. Review the columns and try again.',
  [BulkInviteErrorCode.REUPLOAD_NOT_TERMINAL]:
    'This request is still processing. Wait for it to finish before re-uploading.',
  [BulkInviteErrorCode.ROW_ERRORS]: 'Some rows need attention.',
  [BulkInviteErrorCode.INVALID_NAME]: 'First and last name are required.',
  [BulkInviteErrorCode.INVALID_EMAIL]: 'Not a valid email address.',
  [BulkInviteErrorCode.INVALID_PHONE]: 'Not a valid phone number.',
  [BulkInviteErrorCode.INVALID_ROLE]: 'Role must be “user” or “admin”.',
  [BulkInviteErrorCode.INVALID_STORAGE_QUOTA]:
    'Storage quota must be at least 10 MB (e.g. 500MB, 2GB).',
  [BulkInviteErrorCode.INVALID_API_QUOTA]: 'API quota must be a whole number of at least 1.',
  [BulkInviteErrorCode.DUPLICATE_EMAIL_IN_FILE]: 'This email appears more than once in the file.',
  [BulkInviteErrorCode.EMAIL_CLAIMED]: 'Already invited or registered in your organization.',
  [BulkInviteErrorCode.EMAIL_CLAIMED_EXTERNAL]:
    'Already registered to a QSE account outside your organization.',
}

/** Resolve a code to frontend copy, falling back to the server message, then a generic. */
export const friendlyBulkInviteMessage = (code?: string | null, fallback?: string): string =>
  (code ? BULK_INVITE_ERROR_MESSAGES[code] : undefined) || fallback || GENERIC_BULK_INVITE_ERROR

/**
 * Normalize a bulk-invite error into a predictable, user-presentable shape.
 *
 * Backend errors are wrapped in an `{ error: { code, message, ... } }` envelope, so we
 * unwrap that first (the same convention the shared ApiError uses). Two payload shapes:
 *  - A. structural failure → `message` is a string
 *  - B. multi-part failure → `message.errors` is an array (each entry carries its own `code`)
 *
 * Under the non-blocking model shape B is now essentially the **quota** case (one entry
 * per exhausted dimension). Row failures no longer arrive here at all — they come back as
 * preview data. `fieldErrors` therefore stays for quota, and for tolerating an older
 * backend that still returns BULK_INVITE_ROW_ERRORS.
 */
export const parseBulkInviteError = (error: unknown): ParsedBulkInviteError => {
  const body = (error as { response?: { data?: any } })?.response?.data ?? {}
  // Unwrap the `{ error: {...} }` envelope; fall back to the body itself if absent.
  const payload = (body && typeof body === 'object' && body.error) || body
  const code: string | null = typeof payload?.code === 'string' ? payload.code : null
  const rawMessage = payload?.message
  const serverString = typeof payload?.error === 'string' ? payload.error : undefined

  // Shape B — message.errors[]
  if (rawMessage && typeof rawMessage === 'object' && Array.isArray(rawMessage.errors)) {
    const fieldErrors: BulkInviteFieldError[] = rawMessage.errors.map((entry: any) => {
      const entryCode = typeof entry?.code === 'string' ? entry.code : null
      const serverEntryMessage = typeof entry?.message === 'string' ? entry.message : undefined

      return {
        row: typeof entry?.row === 'number' ? entry.row : undefined,
        field: typeof entry?.field === 'string' ? entry.field : undefined,
        email: typeof entry?.email === 'string' ? entry.email : undefined,
        code: entryCode ?? UNKNOWN_ROW_ERROR_CODE,
        message: friendlyBulkInviteMessage(entryCode, serverEntryMessage),
      }
    })

    return {
      code,
      message: friendlyBulkInviteMessage(code, serverString),
      fieldErrors,
    }
  }

  // Shape A — message is a string (or fall back to server error / friendly / generic)
  const message = friendlyBulkInviteMessage(
    code,
    typeof rawMessage === 'string' ? rawMessage : serverString,
  )

  return { code, message, fieldErrors: [] }
}
